<template>
  <header :class="{ 'is-past': isPast }">
    <aside>
      <img loading="lazy" :src="photoUrl" />
    </aside>
    <div class="site-details">
      <h4 class="cs-name">{{ name }}</h4>
      <p>{{ address }}</p>
      <p class="pitch" v-if="pitchtypeName || categories">
        <pu-category-icons :categories="categories" class="categories" />
        <span v-if="pitchtypeName" class="pt-name">
          {{ pitchtypeName }}
        </span>
      </p>
      <!-- used by booking summary cards -->
      <p v-if="isPast" class="dates">
        <template v-if="isSameMonth">
          <search-summary-bar-details-calendar-same-month
            :date-range="{ arrive: arrive, depart: depart }"
          />
        </template>
        <template v-else>
          <i18n-t keypath="{fromDate} - {toDate}">
            <template #fromDate>
              <pu-utc-date :date="arrive" options="dayMonth" />
            </template>
            <template #toDate>
              <pu-utc-date :date="depart" options="dayMonth" />
            </template>
          </i18n-t>
        </template>
        <pu-date tag="span" class="year" :date="depart" options="year" />
      </p>
      <slot />
    </div>
  </header>
</template>

<script setup lang="ts">
import { CampsiteCategory } from '~/utility/types/CampsiteCategory'

const props = withDefaults(
  defineProps<{
    name: string
    address: string
    arrive?: string
    depart?: string
    photoUrl: string
    pitchtypeName?: string
    categories?: CampsiteCategory[]
    isPast?: boolean
  }>(),
  {
    isPast: false,
  },
)

function isSameMonth() {
  const arrive = new Date(props.arrive)
  const depart = new Date(props.depart)
  return arrive.getMonth() === depart.getMonth()
}
</script>

<style scoped>
header {
  border-bottom: 1px solid var(--colour-table-borders);
  padding-bottom: 0.75em;
  display: flex;
  flex-direction: row;
  justify-content: start;
}

header.is-past {
  padding-bottom: 0.5em;
}

header p {
  color: var(--colour-text-light);
  font-size: var(--body);
  line-height: 1.1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.site-details {
  overflow: hidden;
  flex-basis: 100%;
  display: flex;
  flex-direction: column;
}

.cs-name {
  margin: 0 0 0.15em 0;
  font-weight: bold;
  font-size: var(--large);
  color: var(--colour-dark-blue);
}

.pitch,
.dates {
  margin: 0.5em 0 0 0;
}

.pt-name {
  display: inline;
  color: initial;
}

.dates {
  color: initial;
}

.pitch :deep(.category-icons) {
  display: inline;
}

.categories {
  display: inline-block;
}

aside {
  position: relative;
  background: rgba(15, 30, 65, 0.125);
  border-radius: 0.25em;
  display: block;
  overflow: hidden;
  width: 3.5em;
  height: 3.5em;
  flex-shrink: 0;
  margin: 0 var(--edge-margin) 0 0;
}

aside::after {
  border-radius: inherit;
  box-shadow: inset 0 0 0 1px rgba(15, 30, 65, 0.1);
  content: '';
  height: 100%;
  left: 0;
  position: absolute;
  top: 0;
  width: 100%;
}

aside img {
  height: auto;
  left: 0;
  position: absolute;
  top: 0;
  width: 100%;
}

@supports (object-fit: cover) {
  aside img {
    object-fit: cover;
    height: 100%;
  }
}
</style>
